/**
 * Phase 6 Task 8 — SLA gauge using PrimeReact ProgressBar.
 *
 * Colour thresholds mirror common call-centre SLA targets:
 *   ≥ 90 % → green (success)
 *   70–89 % → yellow (warning)
 *   < 70 % → red (danger)
 */
import type { JSX } from 'react'
import { ProgressBar } from 'primereact/progressbar'

interface Props {
  slaPercent: number
  targetPercent?: number
}

type Severity = 'success' | 'warning' | 'danger'

function severity(sla: number): Severity {
  if (sla >= 90) return 'success'
  if (sla >= 70) return 'warning'
  return 'danger'
}

const SEVERITY_COLOUR: Record<Severity, string> = {
  success: '#22c55e',
  warning: '#eab308',
  danger: '#ef4444',
}

export function SlaGauge({ slaPercent, targetPercent = 90 }: Props): JSX.Element {
  const sev = severity(slaPercent)
  const colour = SEVERITY_COLOUR[sev]

  return (
    <div data-testid="sla-gauge" data-severity={sev} className="flex flex-col gap-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium">SLA</span>
        <span>
          {slaPercent.toFixed(1)}%
          <span className="text-gray-400 ml-1">(target {targetPercent}%)</span>
        </span>
      </div>
      <ProgressBar
        value={slaPercent}
        color={colour}
        showValue={false}
        style={{ height: '12px' }}
      />
    </div>
  )
}
